import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ApiResponse } from '@types';

import {
  getCallSessionById,
  getCallSessionByStatusAndAgentId,
  updateCallSession,
  hangupCallSession,
  updateCallLog,
  getCallLogByCallId,
  getCallInfo,
  getRecordingByCallId,
  getRecordingsBySdrId,
  getRecordingById,
  playRecording,
  updateContactNotes,
  getTwilioToken,
  getCurrentFromNumber,
  setSessionAction,
  twilioTransferCall as transferCallEdge,
  markCallCutoff,
  markCallTransferError,
  type CallSessionSuccessResponse,
  type CallSession as CallSessionDTO, type CallLog, getCallLogsBySessionId,
} from '@services/Common';

import {
  getAgentCallSessionValidations,
  consumeNextContact,
  invalidateCurrentContact,
  type AgentValidationsPayload,
  type ConsumeNextContactResponse,
  type NextContactBody as NextContactBodySvc, invalidateForEndSession,
} from '@services/Agent';

import { logError } from '@lib/logger/logError';
import { logInfo } from '@lib/logger/logInfo';
import { logWarning } from '@lib/logger/logWarning';

type CallSession = CallSessionDTO;

type TransferAction = 'transferred' | 'paused' | 'started' | 'finished';

export type NextContactBody = NextContactBodySvc;
export interface NextContactResponse {
  body?: Partial<NextContactBody>;
  queueLength?: number;
}

export interface UpdateNotesPayload {
  contactId: string;
  callId: string;
  notesAgent?: string | null;
  notesSDR?: string | null;
}

export interface PlayRecordingPayload {
  friendlyNameVM: string;
  recordingUrl: string;
}

export interface TransferPayload {
  callId: string;
  sdrId: string;
  organizationId: string;
  callSessionId: string;
  agentId: string;
}

export interface SetActionPayload {
  action: TransferAction;
  userId: string;
}

type LoadingKey =
  | 'validations'
  | 'consumeNext'
  | 'invalidate'
  | 'invalidateSession'
  | 'sessionByStatus'
  | 'sessionById'
  | 'updateSession'
  | 'hangupSession'
  | 'updateLog'
  | 'callLogByCallId'
  | 'callLogByCallSessionId'
  | 'logsBySession'
  | 'recordingByCallId'
  | 'recordingsBySdrId'
  | 'recordingById'
  | 'playRecording'
  | 'updateNotes'
  | 'twilioToken'
  | 'metrics';

const initialLoading: Record<LoadingKey, boolean> = {
  validations: false,
  consumeNext: false,
  invalidate: false,
  invalidateSession: false,
  sessionByStatus: false,
  sessionById: false,
  updateSession: false,
  hangupSession: false,
  updateLog: false,
  callLogByCallId: false,
  callLogByCallSessionId: false,
  logsBySession: false,
  recordingByCallId: false,
  recordingsBySdrId: false,
  recordingById: false,
  playRecording: false,
  updateNotes: false,
  twilioToken: false,
  metrics: false,
};

export interface UseAgentApiReturn {
  loading: Record<LoadingKey, boolean>;
  error: string | null;
  ensureCanDial: (args: {
    agentId?: string | null;
    callSessionId?: string | null;
    contactId?: string | null;
    contactListId?: string | null;
    number?: string | null;
  }) => Promise<void>;
  agentValidations: (agentId: string) => Promise<ApiResponse<AgentValidationsPayload>>;
  nextContact: (csId: string, agentId: string) => Promise<ApiResponse<ConsumeNextContactResponse>>;
  invalidateContact: (csId: string, agentId: string) => Promise<ApiResponse<boolean>>;
  invalidateSession: (csId: string) => Promise<ApiResponse<boolean>>;
  sessionByStatusAndAgent: (status: string, agentId: string) => Promise<ApiResponse<CallSession[]>>;
  sessionById: (id: string) => Promise<ApiResponse<CallSession>>;
  sessionUpdate: (id: string, body: Record<string, unknown>) => Promise<ApiResponse<CallSession>>;
  sessionHangup: (id: string) => Promise<ApiResponse<CallSession>>;
  callLogUpdate: (body: { callId: string } & Record<string, unknown>) => Promise<ApiResponse<{ ok: boolean }>>;
  callLogByCallId: (callId: string) => Promise<ApiResponse<{ status?: string } | null>>;
  callLogBySessionId: (callSessionId: string) => Promise<ApiResponse<CallLog[]>>;
  callLogsBySession: (sessionId: string) => Promise<ApiResponse<CallLog[]>>;
  recordingByCallId: (callId: string) => Promise<ApiResponse<unknown>>;
  recordingsBySdrId: (sdrId: string) => Promise<ApiResponse<unknown>>;
  recordingById: (id: string) => Promise<ApiResponse<unknown>>;
  playVmOnCall: (payload: PlayRecordingPayload) => Promise<ApiResponse<{ ok: boolean }>>;
  saveContactNotes: (payload: UpdateNotesPayload) => Promise<ApiResponse<{ ok: boolean }>>;
  fetchTwilioToken: (identity?: string) => Promise<ApiResponse<{ token: string; identity: string }>>;
  transferCall: (payload: TransferPayload) => Promise<ApiResponse<{ ok: boolean }>>;
  setAction: (csId: string, action: SetActionPayload) => Promise<ApiResponse<CallSessionSuccessResponse>>;
  markCutOff: (callId: string) => Promise<ApiResponse<boolean>>;
  markTransferError: (callId: string) => Promise<ApiResponse<boolean>>;
}

export function useAgentApi (): UseAgentApiReturn {
  const [loading, setLoading] = useState<Record<LoadingKey, boolean>>(initialLoading);
  const [error, setError] = useState<string | null>(null);

  const setLoadingKey = useCallback((key: LoadingKey, v: boolean): void => {
    setLoading((m) => ({ ...m, [key]: v }));
  }, []);

  type ApiFailure = { success: false } & Pick<ApiResponse<unknown>, 'error'>;

  function isApiFailure<T> (res: ApiResponse<T>): res is ApiFailure {
    return !(res as { success: boolean }).success;
  }

  function getFailureMessage<T> (res: ApiResponse<T>): string | null {
    if (!isApiFailure(res)) {
      return null;
    }
    const msg = res.error?.message;
    return typeof msg === 'string' ? msg : null;
  }

  function getMessageFromUnknown (err: unknown): string {
    if (err instanceof Error) {
      return err.message;
    }
    if (typeof err === 'string') {
      return err;
    }
    if (typeof err === 'object' && err !== null && 'message' in err) {
      const maybe = (err as { message?: unknown }).message;
      if (typeof maybe === 'string') {
        return maybe;
      }
    }
    return 'Unexpected error';
  }

  const wrap = useCallback(
    async <T, >(key: LoadingKey, fn: () => Promise<ApiResponse<T>>): Promise<ApiResponse<T>> => {
      setLoadingKey(key, true);
      setError(null);
      try {
        const res = await fn();
        const msgFromRes = getFailureMessage(res);
        if (msgFromRes !== null) {
          setError(msgFromRes);
          await logWarning(msgFromRes, `useAgentApi/${key}`, undefined, false);
        }
        return res;
      } catch (err: unknown) {
        const msg = getMessageFromUnknown(err);
        setError(msg);
        await logError(msg, `useAgentApi/${key}`, undefined, false);
        const failure: ApiResponse<T> = { success: false, error: { message: msg } };
        return failure;
      } finally {
        setLoadingKey(key, false);
      }
    },
    [setLoadingKey]
  );

  const ensureCanDial = useCallback(async ({
    agentId,
    callSessionId,
    contactId,
    contactListId,
    number,
  }: {
    agentId?: string | null;
    callSessionId?: string | null;
    contactId?: string | null;
    contactListId?: string | null;
    number?: string | null;
  }): Promise<void> => {
    if (agentId == null || callSessionId == null || contactId == null || contactListId == null) {
      throw new Error('missing-context');
    }
    const e164 = (number ?? '').trim();
    if (!e164.startsWith('+') || e164.length < 3) {
      throw new Error('bad-dst');
    }

    const currentFrom = await getCurrentFromNumber();
    if (
      !currentFrom.success ||
      currentFrom.data == null ||
      typeof currentFrom.data.from !== 'string' ||
      currentFrom.data.from.trim() === ''
    ) {
      throw new Error('no-from');
    }

    const sess = await getCallSessionById(callSessionId);
    if (!sess.success || sess.data == null) {
      throw new Error('session-not-found');
    }

    const s: CallSession = sess.data;
    if (s.status !== 'started') {
      throw new Error('paused');
    }
    if ((s.action?.action ?? '').toUpperCase() === 'TRANSFERRED') {
      throw new Error('transfer');
    }
    if (s.sdrStatus != null && s.sdrStatus !== 'free') {
      throw new Error('sdr-busy');
    }
  }, []);

  const agentValidations = useCallback(
    async (agentId: string) =>
      await wrap<AgentValidationsPayload>('validations', async () => await getAgentCallSessionValidations(agentId)),
    [wrap]
  );

  const nextContact = useCallback(
    async (csId: string, agentId: string) =>
      await wrap<ConsumeNextContactResponse>('consumeNext', async () => await consumeNextContact(csId, agentId)),
    [wrap]
  );

  const invalidateContact = useCallback(
    async (csId: string, agentId: string) =>
      await wrap<boolean>('invalidate', async () => await invalidateCurrentContact(csId, agentId)),
    [wrap]
  );

  const invalidateSession = useCallback(
    async (csId: string) =>
      await wrap<boolean>('invalidateSession', async () => await invalidateForEndSession(csId)),
    [wrap]
  );

  const sessionByStatusAndAgent = useCallback(
    async (status: string, agentId: string) =>
      await wrap<CallSession[]>('sessionByStatus', async () => await getCallSessionByStatusAndAgentId(status, agentId)),
    [wrap]
  );

  const sessionById = useCallback(
    async (id: string) =>
      await wrap<CallSession>('sessionById', async () => await getCallSessionById(id)),
    [wrap]
  );

  const sessionUpdate = useCallback(
    async (id: string, body: Record<string, unknown>) =>
      await wrap<CallSession>('updateSession', async () => await updateCallSession(id, body)),
    [wrap]
  );

  const sessionHangup = useCallback(
    async (id: string) =>
      await wrap<CallSession>('hangupSession', async () => await hangupCallSession(id)),
    [wrap]
  );

  type UpdateCallLogBody = { callId: string } & Record<string, unknown>;
  function assertHasCallId (body: UpdateCallLogBody): asserts body is UpdateCallLogBody {
    if (typeof body.callId !== 'string' || body.callId.trim() === '') {
      throw new Error('callId is required');
    }
  }

  const callLogUpdate = useCallback(
    async (body: UpdateCallLogBody) => {
      assertHasCallId(body);
      return await wrap<{ ok: boolean }>('updateLog', async () => await updateCallLog(body));
    },
    [wrap]
  );

  const callLogBySessionId = useCallback(
    async (callSessionId: string) =>
      await wrap<CallLog[]>('callLogByCallSessionId', async () => await getCallLogsBySessionId(callSessionId)),
    [wrap]
  );

  const callLogByCallId = useCallback(
    async (callId: string) =>
      await wrap<{ status?: string } | null>('callLogByCallId', async () => await getCallLogByCallId(callId)),
    [wrap]
  );

  const callLogsBySession = useCallback(
    async (sessionId: string) =>
      await wrap<CallLog[]>('logsBySession', async () => await getCallInfo(sessionId)),
    [wrap]
  );

  const recordingByCallId = useCallback(
    async (callId: string) =>
      await wrap<unknown>('recordingByCallId', async () => await getRecordingByCallId(callId)),
    [wrap]
  );

  const recordingsBySdrId = useCallback(
    async (sdrId: string) =>
      await wrap<unknown>('recordingsBySdrId', async () => await getRecordingsBySdrId(sdrId)),
    [wrap]
  );

  const recordingById = useCallback(
    async (id: string) =>
      await wrap<unknown>('recordingById', async () => await getRecordingById(id)),
    [wrap]
  );

  const playVmOnCall = useCallback(
    async (payload: PlayRecordingPayload) =>
      await wrap<{ ok: boolean }>('playRecording', async () => await playRecording(payload)),
    [wrap]
  );

  const saveContactNotes = useCallback(
    async (payload: UpdateNotesPayload) =>
      await wrap<{ ok: boolean }>('updateNotes', async () => await updateContactNotes(payload)),
    [wrap]
  );

  const fetchTwilioToken = useCallback(
    async (identity?: string) =>
      await wrap<{ token: string; identity: string }>('twilioToken', async () => await getTwilioToken(identity)),
    [wrap]
  );

  const transferCall = useCallback(
    async (payload: TransferPayload) =>
      await wrap<{ ok: boolean }>('updateSession', async () => await transferCallEdge(payload)),
    [wrap]
  );

  const setAction = useCallback(
    async (csId: string, action: SetActionPayload) =>
      await wrap<CallSessionSuccessResponse>('updateSession', async () => await setSessionAction(csId, action)),
    [wrap]
  );

  const markCutOff = useCallback(
    async (callId: string) =>
      await wrap<boolean>('updateLog', async () => await markCallCutoff(callId)),
    [wrap]
  );

  const markTransferError = useCallback(
    async (callId: string) =>
      await wrap<boolean>('updateLog', async () => await markCallTransferError(callId)),
    [wrap]
  );

  const state = useMemo(() => ({ loading, error }), [loading, error]);

  useEffect(() => {
    void (async () => {
      if (error != null) {
        await logWarning(error, 'useAgentApi/state', { loading }, false);
      } else {
        await logInfo('useAgentApi state updated', 'useAgentApi/state', { loading }, false);
      }
    })();
  }, [error, loading]);

  return {
    ...state,
    ensureCanDial,
    agentValidations,
    nextContact,
    invalidateContact,
    invalidateSession,
    sessionByStatusAndAgent,
    sessionById,
    sessionUpdate,
    sessionHangup,
    callLogUpdate,
    callLogByCallId,
    callLogBySessionId,
    callLogsBySession,
    recordingByCallId,
    recordingsBySdrId,
    recordingById,
    playVmOnCall,
    saveContactNotes,
    fetchTwilioToken,
    transferCall,
    setAction,
    markCutOff,
    markTransferError
  };
}
